import { fieldFocusCss } from '@src/components/common/fieldStateStyles';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

interface InlineRenameFieldProps {
  /** The stored name; empty when nothing has been named yet. */
  value: string;
  /**
   * The fallback shown when `value` is empty. Editing opens on it, and it stays
   * the input's placeholder for when the field is cleared.
   */
  placeholder: string;
  /** Called with the trimmed value; empty means "clear the stored name". */
  onCommit: (value: string) => void;
  /**
   * Lets the host suspend behaviour that conflicts with an open input. Fires on
   * mount for an `autoEdit` field, which opens with no click to announce it.
   */
  onEditingChange?: (editing: boolean) => void;
  /**
   * Shows the name as plain text — no click target, no pencil. For hosts where
   * naming is someone else's to do (a translation reviewer reads a clip's
   * title but does not author it), so the row keeps its type and spacing
   * without offering a rename.
   */
  readOnly?: boolean;
  /** Names the control for assistive tech, e.g. "Rename Section 2". */
  ariaLabel: string;
  testId?: string;
  /**
   * False where a richer hover already covers the name — the Reel Editor row's
   * transcript panel (PLAT-11246) — so the browser's own box does not float on
   * top of it. Everywhere else the native tooltip stays: it is the only way to
   * read an ellipsized name in full without opening the field.
   */
  nativeTooltip?: boolean;
  /**
   * Styles the resting text as a heading over the rows beneath it: semi-bold at
   * `--text-secondary`, the same weight and colour the Reel Producer's own
   * header stats carry. Weight is what makes it read as structure; the step down
   * from `--text-primary` is what stops it competing with the Section names,
   * which are the content.
   */
  heading?: boolean;
  /**
   * Opens straight into the input, for a host where the rename is reached from
   * somewhere other than the text itself — a menu item, where the resting text
   * is already spoken for (the transcript's Section headings seek when clicked,
   * PLAT-11257). The host mounts the field only while renaming and takes it
   * down again on `onEditingChange(false)`.
   */
  autoEdit?: boolean;
  /**
   * Which pencil treatment the name gets, because the two hosts this serves
   * have opposite constraints (PLAT-11503):
   *
   * `'row'` (default) — the space-scarce case PLAT-11397 tuned: an 11px glyph
   * overlaying the name's own tail, in the slot the ellipsis vacates, held
   * deliberately close so it reads as this name's control rather than as the
   * start of the kebab column beside it. The Reel Editor's name column is
   * ~140px, so nothing there can be spent on reserved space.
   *
   * `'title'` — a page or modal heading, where there is room and the glyph has
   * no neighbours to be confused with. The pencil sits after the name in the
   * flow, one `--spacing-sm` off it, at the 14px the Content Studio story title
   * uses — the design system asks a leading icon to match its label's scale,
   * and 11px against a 20px heading does not.
   */
  size?: 'row' | 'title';
  /**
   * Whether hovering the name itself reveals the pencil and the wash behind
   * it (default). False for the Reel Editor's rows, where PLAT-11397 took the
   * hover pencil away on purpose: the name column is ~140px, the glyph over its
   * last word was the least legible thing on the row, and the kebab's Edit Name
   * says it in words. Keyboard focus and a host's own reveal rule still show
   * it — only the pointer stops being a trigger.
   */
  hoverReveal?: boolean;
}

/**
 * PLAT-11054 — click to rename, in place. Built for Reel Sections, reused
 * unchanged by Chapter rows (PLAT-11055) and by the video template settings
 * header (PLAT-11219), which is why it knows nothing about any of them: it
 * takes a value, a fallback and a commit.
 *
 * Editing opens on the text the row was showing — the stored name, or the
 * fallback when there is none — so a rename starts from what is there instead of
 * an empty box. Enter or blur commits the trimmed value, Escape cancels.
 *
 * A commit fires only when the value differs from what was shown. That is what
 * keeps an untouched fallback a fallback rather than storing the opening words
 * as a hand-typed name, and it lets an emptied field commit `''` — the way a
 * creator drops their name and falls back again (DS1).
 */
export const InlineRenameField: React.FC<InlineRenameFieldProps> = ({
  value,
  placeholder,
  onCommit,
  onEditingChange,
  ariaLabel,
  testId,
  nativeTooltip = true,
  heading = false,
  readOnly = false,
  autoEdit = false,
  size = 'row',
  hoverReveal = true,
}) => {
  const [isEditing, setIsEditing] = useState(autoEdit);
  // The text the field opens on, whichever way it was opened: the stored name,
  // or the fallback when there is none.
  const [draft, setDraft] = useState(value || placeholder);
  const inputRef = useRef<HTMLInputElement>(null);

  const shownName = value || placeholder;

  const startEditing = () => {
    setDraft(shownName);
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    onEditingChange?.(false);
  };

  const commit = () => {
    stopEditing();
    const trimmed = draft.trim();
    if (trimmed !== shownName) onCommit(trimmed);
  };

  // `autoEdit` is an initial state, so an open the host asked for never fires
  // the `true` a click on the text does — and the host's guards stay off. A
  // row's `draggable` is one of them: a draggable ancestor wins over a pointer
  // drag inside it, so the row was lifted out of the list instead of the name
  // selecting (PLAT-11397). Mount only; every later flip goes through
  // `startEditing` / `stopEditing`.
  useEffect(() => {
    if (autoEdit) onEditingChange?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    // Never take focus off the renderer iframe: the iframe-deactivation
    // reaction dispatches FORCED_REFRESH, which can revert an in-flight canvas
    // edit (CLAUDE.md pitfall 10, PLAT-10700) — and the canvas is one pane away
    // in this modal. A click into this field has already blurred the iframe, so
    // in practice this only skips the autofocus for a programmatic open.
    if (document.activeElement instanceof HTMLIFrameElement) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  // Before the editing branch, so a host that turns this on mid-edit drops
  // back to text rather than leaving an input nobody may commit.
  if (readOnly) {
    return (
      <Trigger
        as="span"
        $heading={heading}
        $size={size}
        $hoverReveal={false}
        $readOnly
        title={nativeTooltip ? shownName : undefined}
        data-testid={testId}
      >
        <Text data-rename-text>{shownName}</Text>
      </Trigger>
    );
  }

  if (isEditing) {
    return (
      <Field
        ref={inputRef}
        $size={size}
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid={testId && `${testId}-input`}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            // Hosts that live in a modal close on a window-level Escape, so
            // cancelling a rename would take the modal down with it.
            e.stopPropagation();
            stopEditing();
          }
        }}
      />
    );
  }

  return (
    <Trigger
      type="button"
      $heading={heading}
      $size={size}
      $hoverReveal={hoverReveal}
      title={nativeTooltip ? shownName : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={startEditing}
    >
      <Text data-rename-text>{shownName}</Text>
      {/* Revealed by hovering or keyboard-focusing the name itself, and by a
          host row's own hover rule where it has one. */}
      <Pencil data-rename-pencil aria-hidden="true">
        {/* Matches the label it sits beside: the row's 11px default, or the
            14px the Content Studio story title carries at heading scale. */}
        {size === 'title' ? (
          <SimplePencilIcon width="14" height="14" />
        ) : (
          <SimplePencilIcon />
        )}
      </Pencil>
    </Trigger>
  );
};

/**
 * The custom property a host sets to reveal the pencil — on its own hover, or
 * while it holds a menu open for the row. A property rather than a rule on the
 * pencil itself (PLAT-11397): the name has to know too, so it can hand over the
 * room the pencil needs instead of being overlapped by it.
 */
export const PENCIL_REVEAL = '--rename-pencil-reveal';

/** The row's name type scale, shared by the resting text and the input. */
const nameTextCss = css`
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  color: var(--text-primary);
`;

/**
 * The box both states share. Identical padding and border width is what keeps
 * the name from shifting as the field opens — change one and you change both.
 */
const fieldBoxCss = css`
  padding: 1px 5px;
  border-width: 1px;
  border-style: solid;
  border-radius: var(--radius-input);
  font-family: var(--font-primary);
`;

/** An inline-editable row: the bordered-field state pattern, at row height. */
const Field = styled.input<{ $size: 'row' | 'title' }>`
  ${nameTextCss}
  ${fieldBoxCss}
  width: 100%;
  min-width: 0;
  background-color: var(--input-background);
  border-color: var(--border);
  transition: border-color var(--transition-fast);

  &::placeholder {
    color: var(--text-muted);
  }

  /* The app's own selection tint. Without a rule here the field fell back to
     the browser's default blue — a colour from outside the palette, and the one
     thing on the surface not answering to a token, since a rename opens with
     the whole name already selected (PLAT-11503). Every other inline title
     field already sets this. */
  &::selection {
    background: var(--primary-tint);
  }

  &:hover {
    border-color: var(--text-muted);
  }

  ${fieldFocusCss}
  ${props => props.$size === 'title' && titleBoxCss}
`;

/**
 * The box at heading scale, shared by the resting text and the open input for
 * the same reason `fieldBoxCss` is — change it for one and the name jumps as
 * the field opens.
 *
 * Row padding is 1px, which is right for a 13px name in a list. At a 20px
 * heading the line box is ~26px, so 1px leaves the selection highlight sitting
 * flush against the border: its square corners then cut visibly across the
 * field's rounded ones the moment a rename opens with the name selected. 3px
 * clears the curve, and is what the Content Studio story title uses.
 */
const titleBoxCss = css`
  padding: 3px 7px;
`;

/* Also the read-only state, rendered `as="span"`: sharing the box is what keeps
   the name in the same place whether or not the host allows a rename. */
const Trigger = styled.button<{
  $heading: boolean;
  $readOnly?: boolean;
  $size: 'row' | 'title';
  $hoverReveal: boolean;
}>`
  ${fieldBoxCss}
  position: relative;
  display: flex;
  align-items: center;
  /* Hugs the name so the pencil sits beside it rather than out at the row's
     edge, capped at the row width so a long name still ellipsizes. */
  align-self: flex-start;
  max-width: 100%;
  min-width: 0;
  /* Borderless at rest so the row reads as a list, not a form; the border
     appears with the input, in the same place. */
  border-color: transparent;
  background: none;
  text-align: left;
  cursor: ${props => (props.$readOnly ? 'inherit' : 'text')};
  transition: background var(--transition-fast);

  &:focus-visible {
    ${PENCIL_REVEAL}: 1;
  }

  &:hover [data-rename-pencil] {
    color: var(--text-primary);
  }

  /* The click-to-edit affordance, the same one the Content Studio story title
     has always had: a subtle wash and the pencil, so the name reads as editable
     before it is clicked (PLAT-11503). Backtick-free on purpose — this sits
     inside a template literal.

     It belongs here rather than in each host. The reveal property lets a host
     reveal the pencil from a wider trigger — its whole row, or while it holds
     that row's menu open — but only two of them ever wrote that rule, so on
     every other surface, the Clip Preview title included, the pencil was
     unreachable and the rename undiscoverable. A host rule now widens the
     reveal rather than being the only thing that grants it.

     Skipped for the read-only state, which renders as a span: a wash and a
     pencil there would promise a rename that is not on offer. And skipped
     where the host says so (hoverReveal false): the Reel Editor's rows, whose
     PLAT-11397 rule is that nothing on the row answers a mere hover. */
  ${props =>
    !props.$readOnly &&
    props.$hoverReveal &&
    css`
      &:hover {
        ${PENCIL_REVEAL}: 1;
        background: var(--sidebar-hover);
      }
    `}

  /* The heading treatment (PLAT-11503): the pencil rejoins the flow, one
     spacing step off the name, at the muted weight the Content Studio story
     title's carries. Nothing is overlaid and nothing is reserved, so the name
     keeps its own ellipsis and the row-scale slot trick is switched off.
     Backtick-free on purpose — this sits inside a template literal. */
  ${props =>
    props.$size === 'title' &&
    css`
      ${titleBoxCss}
      gap: var(--spacing-sm);

      [data-rename-pencil] {
        position: static;
        transform: none;
      }

      /* Muted, and it stays muted with the pointer on the name. The row
         variant's two-step brighten answers a case this one does not have:
         there, a host can reveal the pencil from the whole row while the
         pointer is elsewhere, so the glyph needs a second, brighter state to
         say the pointer has arrived. Here the name's own hover is the only
         thing that ever reveals it, so there is one state — and it is the one
         the Content Studio story title shows. Ordered after the brighten rule
         above deliberately: equal specificity, so source order decides. */
      [data-rename-pencil],
      &:hover [data-rename-pencil] {
        color: var(--text-muted);
      }

      ${Text} {
        margin-right: 0;
      }
    `}

  /* Heading styling at rest only: once the input is open the text is what the
     creator is typing, so it takes the ordinary field weight and colour —
     typing into bold grey reads as a label rather than an editable value. */
  ${props =>
    props.$heading &&
    css`
      ${Text} {
        font-weight: var(--font-weight-semi-bold);
        color: var(--text-secondary);
      }
    `}
`;

const Text = styled.span`
  ${nameTextCss}
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  /* The pencil's slot, opened only while it is revealed: the 11px glyph at
     right: 4px, plus a 4px gap so it reads as beside the name rather than as its
     last letter. A margin and not a padding, because overflow: hidden clips at
     the padding edge — a padded slot let a long name run on under the pencil
     with the glyph drawn over its tail (PLAT-11503). Shrinking the box instead
     moves the ellipsis in front of the pencil, at the cost of one glyph of a
     name that was already clipped.

     Nothing is held at rest, so the resting name still has the whole column —
     ~140px of it in the Reel Editor's panel, which is why none of this can be
     spent on a glyph nobody is looking at yet. One custom property carries the
     whole reveal (this and the pencil's own opacity), which is why the hosts
     have one line to write. Backtick-free on purpose — this sits inside a
     template literal. */
  margin-right: calc(var(${PENCIL_REVEAL}, 0) * 14px);
  transition: margin-right var(--transition-fast);
`;

/*
 * Out of the flow, over the name's own right edge (PLAT-11397): in the Reel
 * Editor's panel the name has ~140px to say itself in, and a glyph that is
 * invisible until the row is hovered was holding 17px of that — its 12px plus
 * the row's gap — on every row, all the time. Reserving space for it cost more
 * of the title than it saved in tidiness.
 *
 * Absolute rather than in the flow so the resting name is never paying for it.
 * What it needs on hover the name hands over instead (see `Text`), and only that
 * — it lands in the slot the ellipsis vacates, against the text rather than out
 * at the column's edge.
 *
 * A step brighter than the row's other muted glyphs while it is up: it is 11px
 * of line-art arriving beside text at the same weight, and at `--text-muted` it
 * read as part of the name's tail rather than as something to click. Primary
 * once the pointer is on the name itself, the way the rows' chevrons answer.
 */
const Pencil = styled.span`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
  opacity: var(${PENCIL_REVEAL}, 0);
  transition: opacity var(--transition-fast);
`;
